// A multi-day job's days, on the engineer's phone (Session BDM).
//
// ONE JOB over several working days. Each day the engineer logs ARRIVED when they get to site and LEFT when they go, at whatever times they
// were: that is the record of their hours and the proof they were there. A POP tag tap is NOT needed (nothing here asks for one), and a
// short note is optional. If they stayed late they can BOOK OVERTIME for the day with the time they actually left; without that, a long
// day is still one day. The job's one job sheet (work carried out, parts, sign-off) is completed on the last day, as for any job, below.
//
// NOTHING HERE IS A GATE: a missing signal only delays the record (the save waits in a queue on the phone), and no figure of money,
// price or pay is ever shown. Pinned by WorkDaysPanel.test.tsx.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useEngineerData } from "./EngineerDataContext";
import type { JobView } from "../../lib/offline/types";
import { currentWorkDay, type EngineerWorkDay } from "../../lib/engineerWorkDays";
import { dismissRefusedWorkDayTime, queuedWorkDayTimes, refusedWorkDayTimes, type RefusedWorkDayTime } from "../../lib/workDayTimesQueue";
import { LATE_REASONS, LATE_REASON_CODES, LATE_REASON_TEXT_MAX, VISIT_DATE_HARD_DAYS, VISIT_DATE_SOFT_DAYS, calendarDaysBetween, lateReasonGiven, type LateReasonCode } from "../../lib/visitDateWindow";
import { localDateKey } from "../../lib/engineerCalendar";
import { ConfirmButton } from "../../components/ConfirmButton";

const HM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** "08:05" in the phone's local time from an ISO instant. */
export function isoToHm(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** The instant for "HH:MM" on a "YYYY-MM-DD" day, in the phone's local time; null when it cannot be read. */
export function hmToIso(day: string, hm: string): string | null {
  const m = HM.exec(hm.trim());
  const [y, mo, d] = day.split("-").map(Number);
  if (!m || !y || !mo || !d) return null;
  return new Date(y, mo - 1, d, Number(m[1]), Number(m[2])).toISOString();
}

const dayWords = (day: string): string => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

function summary(wd: EngineerWorkDay): string {
  const t = wd.time;
  if (!t || !t.arrivedAt) return "Not recorded yet";
  const left = t.leftAt ? `left ${isoToHm(t.leftAt)}` : "not left yet";
  return `Arrived ${isoToHm(t.arrivedAt)}, ${left}${t.overtimeBooked ? (t.overtimeApproved ? ", overtime approved" : ", overtime asked for") : ""}`;
}

/** What the office has done with the overtime asked for on the day (Session OTA). Nothing when none was asked for. No money, ever. */
export function overtimeStatusWords(t: EngineerWorkDay["time"]): string | null {
  if (!t || !t.overtimeBooked) return null;
  return t.overtimeApproved ? "Overtime approved" : "Overtime asked for, waiting for the office";
}

/** The times a refused save carried, in words ("arrived 07:40, left 18:10, overtime booked"); null when there is nothing to show. */
export function refusedTimesWords(r: Pick<RefusedWorkDayTime, "payload">): string | null {
  const p = r.payload;
  if (!p) return null;
  const parts: string[] = [];
  if (p.arrivedAt) parts.push(`arrived ${isoToHm(p.arrivedAt)}`);
  if (p.leftAt) parts.push(`left ${isoToHm(p.leftAt)}`);
  if (p.overtimeBooked) parts.push("overtime booked");
  if (p.note && p.note.trim()) parts.push(`note: ${p.note.trim()}`);
  return parts.length > 0 ? parts.join(", ") : null;
}

export default function WorkDaysPanel({ job, now = new Date() }: { job: Pick<JobView, "id" | "workDays" | "scheduledFor">; now?: Date }) {
  const { saveWorkDayTime, online } = useEngineerData();
  const [params] = useSearchParams();
  const todayYmd = localDateKey(now);
  const days = useMemo(() => job.workDays ?? [], [job.workDays]);
  const requested = params.get("day");
  const initial = days.find((d) => d.day === requested) ?? currentWorkDay({ id: job.id, status: "", scheduledFor: job.scheduledFor, workDays: days }, todayYmd);
  const [selected, setSelected] = useState<string | null>(initial?.day ?? null);
  const wd = days.find((d) => d.day === selected) ?? null;

  const [arrived, setArrived] = useState("");
  const [left, setLeft] = useState("");
  const [note, setNote] = useState("");
  const [overtime, setOvertime] = useState(false);
  const [lateReason, setLateReason] = useState<LateReasonCode | "">("");
  const [lateText, setLateText] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "wait" | "error"; text: string } | null>(null);
  const [refused, setRefused] = useState<RefusedWorkDayTime[]>(() => refusedWorkDayTimes().filter((r) => r.jobId === job.id));

  // The form shows what is recorded for the chosen day.
  useEffect(() => {
    setArrived(isoToHm(wd?.time?.arrivedAt ?? null));
    setLeft(isoToHm(wd?.time?.leftAt ?? null));
    setNote(wd?.time?.note ?? "");
    setOvertime(wd?.time?.overtimeBooked === true);
    setLateReason("");
    setLateText("");
    setMessage(null);
  }, [wd?.day, wd?.time?.arrivedAt, wd?.time?.leftAt, wd?.time?.note, wd?.time?.overtimeBooked]);

  if (days.length === 0) return null;

  const future = wd != null && wd.day > todayYmd;
  const ageDays = wd ? calendarDaysBetween(wd.day, todayYmd) : 0;
  const tooOld = ageDays > VISIT_DATE_HARD_DAYS;
  const needsLateReason = ageDays > VISIT_DATE_SOFT_DAYS && !tooOld;
  const waiting = wd ? queuedWorkDayTimes().some((q) => q.jobId === job.id && q.day === wd.day) : false;

  const stamp = (set: (v: string) => void) => {
    const d = new Date();
    set(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
  };

  async function save() {
    if (!wd) return;
    const arrivedIso = arrived.trim() ? hmToIso(wd.day, arrived) : null;
    const leftIso = left.trim() ? hmToIso(wd.day, left) : null;
    if (arrived.trim() && !arrivedIso) return setMessage({ kind: "error", text: "Type the arrival time like 08:30." });
    if (left.trim() && !leftIso) return setMessage({ kind: "error", text: "Type the time you left like 16:45." });
    if (leftIso && !arrivedIso) return setMessage({ kind: "error", text: "Add when you arrived as well." });
    if (arrivedIso && leftIso && new Date(leftIso) <= new Date(arrivedIso)) return setMessage({ kind: "error", text: "You must have left after you arrived." });
    if (overtime && !leftIso) return setMessage({ kind: "error", text: "To book overtime, put the time you actually left." });
    if (needsLateReason && !lateReasonGiven(lateReason, lateText)) return setMessage({ kind: "error", text: "This day is more than a week old. Choose why it is being recorded late." });
    if (!arrivedIso && !leftIso && !note.trim()) return setMessage({ kind: "error", text: "Add when you arrived, or a note." });
    setBusy(true);
    setMessage(null);
    try {
      const reached = await saveWorkDayTime(job.id, wd.day, {
        ...(arrivedIso ? { arrivedAt: arrivedIso } : {}),
        ...(left.trim() ? { leftAt: leftIso } : wd.time?.leftAt ? { leftAt: null } : {}),
        note: note.trim() ? note.trim() : null,
        overtimeBooked: overtime,
        ...(needsLateReason ? { lateReason: lateReason as string, lateReasonText: lateText } : {}),
      });
      setMessage(reached ? { kind: "ok", text: "Saved." } : { kind: "wait", text: "Saved on this phone. It will go to the office as soon as there is signal." });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? err.message : "That could not be saved. Please ring the office." });
    } finally {
      setBusy(false);
      setRefused(refusedWorkDayTimes().filter((r) => r.jobId === job.id));
    }
  }

  // Session R4F (review M3): a refused day's times are kept on the phone. Send them again (the office may have put the engineer back on
  // that day), or copy them to pass to the office; the entry goes only when it has been taken or the engineer dismisses it.
  async function sendAgain(r: RefusedWorkDayTime) {
    if (!r.payload) return;
    setBusy(true);
    setMessage(null);
    try {
      const reached = await saveWorkDayTime(r.jobId, r.day, r.payload);
      dismissRefusedWorkDayTime(r.jobId, r.day);
      setMessage(reached ? { kind: "ok", text: `${dayWords(r.day)} saved.` } : { kind: "wait", text: "Saved on this phone. It will go to the office as soon as there is signal." });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? err.message : "That could not be saved. Please ring the office." });
    } finally {
      setBusy(false);
      setRefused(refusedWorkDayTimes().filter((x) => x.jobId === job.id));
    }
  }
  async function copyForOffice(r: RefusedWorkDayTime, kept: string) {
    const text = `${dayWords(r.day)}: ${kept}`;
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ kind: "ok", text: "Copied. Paste it into a message to the office." });
    } catch {
      setMessage({ kind: "wait", text: `Could not copy. Tell the office: ${text}` });
    }
  }

  const field = "min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-inset px-3 text-ele-strong";
  const btn = "min-h-11 rounded-ele border border-ele-blue px-4 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50";

  return (
    <section aria-labelledby="work-days-heading" className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-card p-5" data-testid="work-days-panel">
      <h2 id="work-days-heading" className="font-heading text-lg text-ele-strong">
        Your days on this job
      </h2>
      <p className="mt-1 text-sm text-ele-body">
        One job over {days[0]!.total} days. Each day, put when you arrived and when you left. You do not need to tap the tag. The job sheet below is filled in once, on the last day.
      </p>

      {refused.map((r) => {
        const kept = refusedTimesWords(r);
        return (
          <div key={`${r.jobId}${r.day}`} role="alert" className="mt-3 rounded-ele border border-amber-400 bg-ele-inset p-3 text-sm text-ele-strong" data-testid="work-day-refused">
            <p>
              {dayWords(r.day)} was not saved: {r.message}
            </p>
            {kept && (
              <p className="mt-1" data-testid="work-day-refused-times">
                Kept on this phone: {kept}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {r.payload && (
                <button type="button" className="min-h-11 rounded-ele border border-ele-card-border px-3 text-sm" disabled={busy} onClick={() => void sendAgain(r)}>
                  Send again
                </button>
              )}
              {kept && (
                <button type="button" className="min-h-11 rounded-ele border border-ele-card-border px-3 text-sm" onClick={() => void copyForOffice(r, kept)}>
                  Copy for the office
                </button>
              )}
              {/* Dismissing removes the kept times from the phone for good, so it asks twice (the branded two-step confirm). */}
              <ConfirmButton
                label="Dismiss"
                confirmLabel="Remove these times?"
                className="min-h-11 rounded-ele border border-ele-card-border px-3 text-sm"
                onConfirm={() => {
                  dismissRefusedWorkDayTime(r.jobId, r.day);
                  setRefused(refusedWorkDayTimes().filter((x) => x.jobId === job.id));
                }}
              />
            </div>
          </div>
        );
      })}

      <ul className="mt-3 grid gap-2" aria-label="Working days">
        {days.map((d) => {
          const isFuture = d.day > todayYmd;
          const active = d.day === selected;
          return (
            <li key={d.workDayId}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => setSelected(d.day)}
                className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-ele border px-3 py-2 text-left ${active ? "border-ele-purple bg-ele-inset" : "border-ele-card-border"} ${isFuture ? "opacity-70" : ""}`}
              >
                <span>
                  <span className="block font-heading text-sm text-ele-strong">
                    {d.label} · {dayWords(d.day)}
                    {d.day === todayYmd ? " · Today" : ""}
                  </span>
                  <span className="block text-xs text-ele-body">{isFuture ? "Not yet" : summary(d)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {wd && future && <p className="mt-3 text-sm text-ele-body">{wd.label} has not happened yet, so there is nothing to record.</p>}
      {wd && tooOld && <p className="mt-3 text-sm text-ele-strong">This day is now over 2 weeks old. Contact the office, who will record it.</p>}

      {wd && !future && !tooOld && (
        <div className="mt-4 grid gap-3">
          <h3 className="font-heading text-base text-ele-strong">
            {wd.label} · {dayWords(wd.day)}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="wd-arrived" className="mb-1 block text-xs uppercase tracking-wide text-ele-body">
                Arrived
              </label>
              <input id="wd-arrived" inputMode="numeric" placeholder="08:30" value={arrived} onChange={(e) => setArrived(e.target.value)} className={field} />
              <button type="button" className={`${btn} mt-2 w-full`} onClick={() => stamp(setArrived)}>
                Arrived now
              </button>
            </div>
            <div>
              <label htmlFor="wd-left" className="mb-1 block text-xs uppercase tracking-wide text-ele-body">
                Left
              </label>
              <input id="wd-left" inputMode="numeric" placeholder="16:45" value={left} onChange={(e) => setLeft(e.target.value)} className={field} />
              <button type="button" className={`${btn} mt-2 w-full`} onClick={() => stamp(setLeft)}>
                Left now
              </button>
            </div>
          </div>

          <label className="flex min-h-11 items-center gap-3 text-sm text-ele-strong">
            <input type="checkbox" checked={overtime} onChange={(e) => setOvertime(e.target.checked)} className="h-5 w-5" />
            <span>I stayed late: ask for overtime for this day (put the time you actually left)</span>
          </label>
          {overtimeStatusWords(wd.time) && (
            <p className="-mt-1 text-sm text-ele-body" data-testid="overtime-status">
              {overtimeStatusWords(wd.time)}
              {wd.time?.overtimeBooked && !wd.time?.overtimeApproved ? ". If you change the times, the office looks at it again." : ""}
            </p>
          )}

          <div>
            <label htmlFor="wd-note" className="mb-1 block text-xs uppercase tracking-wide text-ele-body">
              A short note (optional)
            </label>
            <textarea id="wd-note" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} className={`${field} py-2`} />
            <p className="mt-1 text-xs text-ele-body">Photos can be added on the job sheet below.</p>
          </div>

          {needsLateReason && (
            <div>
              <label htmlFor="wd-late" className="mb-1 block text-xs uppercase tracking-wide text-ele-body">
                Why is this day being recorded late?
              </label>
              <select id="wd-late" value={lateReason} onChange={(e) => setLateReason(e.target.value as LateReasonCode | "")} className={field}>
                <option value="">Choose a reason</option>
                {LATE_REASON_CODES.map((c) => (
                  <option key={c} value={c}>
                    {LATE_REASONS[c]}
                  </option>
                ))}
              </select>
              {lateReason === "other" && (
                <input aria-label="Tell the office why" maxLength={LATE_REASON_TEXT_MAX} value={lateText} onChange={(e) => setLateText(e.target.value)} className={`${field} mt-2`} />
              )}
            </div>
          )}

          <button type="button" disabled={busy} onClick={() => void save()} className="min-h-11 rounded-ele bg-ele-purple px-4 font-heading text-sm tracking-wide text-ele-white disabled:opacity-50">
            {busy ? "Saving..." : "Save this day"}
          </button>
          {waiting && <p className="text-sm text-ele-body">Waiting on this phone to go to the office{online ? "" : " (no signal)"}.</p>}
          {message && (
            <p role={message.kind === "error" ? "alert" : "status"} className={`text-sm ${message.kind === "error" ? "text-ele-strong" : "text-ele-body"}`}>
              {message.text}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
